<script setup>
import { ref, watch, nextTick, inject } from 'vue'
import '@digirati/canvas-panel-web-components'

const props = defineProps({
  manifestUrl: {
    type: String,
    default: 'https://data.getty.edu/media/manifest/bayard-custom'
  },
  canvasIndex: {
    type: Number,
    default: undefined
  }
})

const emits = defineEmits(['viewer-ready'])

const sequenceMargin = ref(0) //gutter between canvases

const panel = ref(null)

const manifest = ref(undefined)
let structures = {}
let currentSequence = []

/**
 * Loads the manifest into vault
 */
const loadManifest = async () => {
  await nextTick()

  if (manifest.value === undefined && props.manifestUrl) {
    manifest.value = await panel.value.vault.loadManifest(props.manifestUrl)
  }
}

const onSequenceReady = async () => {
  panel.value.sequence.setCurrentCanvasIndex(props.canvasIndex || 0)
  await setStructures()
}

/**
 * create map between sequence manifest uri's and structure manifest uri's
 * in order to access structure metadata
 */
const setStructures = async () => {
  structures = {}
  panel.value.vault.get(props.manifestUrl)?.structures.forEach((e) => {
    panel.value.vault.get(e).items.forEach((i) => {
      structures[i.id] = e.id
    })
  })
}

const handleCurrentSequence = (e) => {
  // console.log(e)
  let seq = panel.value.sequence.sequence[e.detail.index]

  currentSequence = []
  seq.forEach((e) => {
    const canvasID = panel.value.sequence.items[e].id
    const structureID = structures[canvasID]

    if (structureID) {
      currentSequence.push(panel.value.vault.get(structureID))
    }
  })

  e.currentSequence = currentSequence
  // console.log('Sequence change!', e)
}

watch(() => props.canvasIndex, (newIndex) => {
  panel.value.sequence.setCurrentCanvasIndex(newIndex)
})

const zoomCoords = inject('zoomCoords', () => ref(null))

watch(zoomCoords, (coords) => {
  if (coords && panel.value) {
    panel.value.goToTarget(coords)
  } else if (panel.value) {
    panel.value.goHome()
  }
})

defineExpose({
  panel,
  manifest
})

</script>

<template>
  <sequence-panel
    id="panel"
    ref="panel"
    :manifest-id="manifestUrl"
    :margin="sequenceMargin"
    :startCanvas="canvasIndex"
    @ready.once="loadManifest"
    @sequence.once="onSequenceReady"
    @sequence-change="handleCurrentSequence($event)"
    @world-ready="emits('viewer-ready')"
    class="h-full"
  >
    <slot name="controls" />
  </sequence-panel>
</template>
